import { useEffect, useState } from 'react';
import { useVinVrmSearchQuery } from './useVehicleSearchQueries';
import { useVehicleSearchContext } from './useVehicleSearchContext';
import { getVehicleDisplayInfo } from '../utils/getVehicleDisplayInfo';
import { vehicleSearchErrorKind, VEHICLE_SEARCH_ERROR_COPY } from '../utils/vehicleSearchError';
import type { VehicleSearchErrorKind } from '../utils/vehicleSearchError';
import type { Vehicle, VinVrmSearchInput } from '../types/vinvrmsearch.types';

const NO_VEHICLE_MESSAGE = 'No vehicle found for this VIN/VRM';

const foundVehicleMessage = (vehicle: Vehicle): string => {
    const info = getVehicleDisplayInfo(vehicle);
    return `Found vehicle: ${info.make} ${info.model}`;
};

export const useVinVrmSearchWithQuery = () => {
    const { selectVehicle, addRecentSearch } = useVehicleSearchContext();
    const [searchInput, setSearchInput] = useState<VinVrmSearchInput>({
        vin: '',
        vrm: '',
    });
    const [isSearchEnabled, setIsSearchEnabled] = useState(false);
    const [message, setMessage] = useState<string>('');
    const [searchError, setSearchError] = useState<VehicleSearchErrorKind | null>(null);

    // Use the query hook with caching
    const {
        data: searchResult,
        isLoading: isSearchLoading,
        error,
        isFetching,
    } = useVinVrmSearchQuery(searchInput, isSearchEnabled);

    // Handle search trigger
    const onVinVrmSearch = async (vinVrm: VinVrmSearchInput) => {
        setMessage('Searching for vehicle...');
        setSearchError(null);
        setSearchInput(vinVrm);
        setIsSearchEnabled(true); // This triggers the query
    };

    // Handle search results
    useEffect(() => {
        if (searchResult && isSearchEnabled) {
            setSearchError(null);

            if (searchResult.vehicle) {
                setMessage(foundVehicleMessage(searchResult.vehicle));
                addRecentSearch(`${searchInput.vin || searchInput.vrm}`);
            } else {
                setMessage(NO_VEHICLE_MESSAGE);
            }
            setIsSearchEnabled(false); // Reset for next search
        }
    }, [searchResult, isSearchEnabled, addRecentSearch, searchInput]);

    // Handle errors
    useEffect(() => {
        const kind = vehicleSearchErrorKind(error);
        if (!kind) {
            return;
        }
        setMessage(VEHICLE_SEARCH_ERROR_COPY[kind]);
        setSearchError(kind);
        setIsSearchEnabled(false);
    }, [error]);

    const onVehicleSelect = (vehicle: Vehicle) => {
        selectVehicle(vehicle);
    };

    return {
        isLoading: false,
        isSearchLoading: isSearchLoading || isFetching,
        message,
        vehicles: searchResult?.vehicle ? [searchResult.vehicle] : [],
        onSearch: onVinVrmSearch,
        onVehicleSelect,
        searchError,
    };
};
